import { Loader, RadioTower, RefreshCw, ShieldAlert, Ticket as TicketIcon } from "lucide-react";
import { DateTime } from "luxon";
import { useMemo } from "react";

import { Button } from "@/components/ui/button";
import { notify } from "@/lib/notify";

import { type ClaimWithRelations, useClaim } from "../../api/use-claim";
import {
  useAssessmentInsight,
  useConfirmIncident,
  useGenerateClaimAssessment,
  useIncidentInsight,
} from "../../api/use-claim-insights";
import { getAiEligibilityVerdict } from "../../lib/ai-verdict";
import {
  type AiFlightEntry,
  type AiIncidentData,
  type LiveFlightMonitoring,
  liveMonitoringLabel,
} from "../../lib/incident-delta";
import { ClaimCircumstances, getRelevantCx } from "../circumstances/claim-circumstances";
import { TicketHistory } from "../passengers/ticket-history";
import { AbandonClaimButton } from "./abandon-claim-button";
import { AiIncidentPreview, type AiIncidentPreviewConfirmData } from "./ai-incident-preview";
import { AiInsightsPanel } from "./ai-insights-panel";

/**
 * Incident study workflow: renders the assessment-derived itinerary comparison
 * plus the "Critères d'éligibilité" toolbox, and confirms the stored INCIDENT
 * insight. Port of InitIncidentWorkflow.vue (+ the V3 fallback shell of
 * InitIncidentWorkflowV3.vue for claims that predate the assessment pipeline).
 */

interface InitIncidentWorkflowProps {
  claimId: string;
  onIncidentConfirmed?: () => void;
}

/** "Billets électroniques" — per-passenger e-tickets + statuses (same TicketHistory as the passengers tab). */
function EticketsSection({ claim }: { claim: ClaimWithRelations }) {
  return (
    <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <h4 className="flex items-center gap-2 text-lg font-semibold tracking-tight text-slate-800">
        <TicketIcon className="h-5 w-5 text-blue-600" />
        Billets électroniques
      </h4>
      <p className="mt-1 text-sm text-slate-500">E-Tickets pour chaque passager et leurs statuts.</p>
      <TicketHistory
        claimId={claim.id}
        impactedFlightId={claim.legalSpecification?.impactedSegment?.flightId ?? null}
      />
    </div>
  );
}

/** Bottom section of the study: detected CX, or an explicit "none detected" empty state. */
function ClaimCxSection({ claimId }: { claimId: string }) {
  const { data: claim } = useClaim(claimId);
  const { active, discarded } = getRelevantCx(claim);

  return (
    <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <h4 className="mb-4 flex items-center gap-2 text-lg font-semibold text-slate-800">
        <ShieldAlert className="h-5 w-5 text-amber-600" />
        Circonstances extraordinaires
      </h4>
      {active.length + discarded.length > 0 ? (
        <ClaimCircumstances claimId={claimId} dense forceFull />
      ) : (
        <p className="text-sm italic text-slate-500">Aucune circonstance extraordinaire détectée.</p>
      )}
    </div>
  );
}

/**
 * Live monitoring of the detected flight, from the claim's own flight record — the insight can
 * predate the monitoring capture (auto-generation races the flight-monitoring update). Delays are
 * computed on the UTC pair; the flight's *Local columns aren't reliably localized.
 */
function liveMonitoringOf(claim: ClaimWithRelations | undefined): LiveFlightMonitoring | null {
  const flight = claim?.legalSpecification?.effectiveFlight;
  if (!flight?.monitoringStatus) {
    return null;
  }
  // SDK timestamps arrive as Date objects — normalize to ISO strings for the lib type.
  const iso = (value?: Date | string | null) => (value ? new Date(value).toISOString() : null);
  const minutesBetween = (from?: Date | string | null, to?: Date | string | null) =>
    from && to ? Math.round((new Date(to).getTime() - new Date(from).getTime()) / 60000) : null;
  return {
    status: flight.monitoringStatus,
    flightIdentifier: flight.flightIdentifier,
    scheduledDepartureUTC: iso(flight.scheduledDepartureUTC),
    scheduledArrivalUTC: iso(flight.scheduledArrivalUTC),
    actualDepartureUTC: iso(flight.actualDepartureUTC),
    inGateArrivalUTC: iso(flight.inGateArrivalUTC),
    arrivalDelayMinutes: minutesBetween(flight.scheduledArrivalUTC, flight.inGateArrivalUTC),
    departureDelayMinutes: minutesBetween(flight.scheduledDepartureUTC, flight.actualDepartureUTC),
  };
}

/**
 * Convert a live UTC timestamp into the insight's naive-local time referential, using the offset
 * between the entry's local scheduled time and the flight's UTC scheduled time. This lets the card
 * render live actuals with its existing delayed/annulé UI without mixing timezones.
 */
function toEntryLocal(
  utcValue: string | null | undefined,
  utcScheduledRef: string | null | undefined,
  localScheduledRef: string | null | undefined,
): string | null {
  if (!utcValue || !utcScheduledRef || !localScheduledRef) {
    return null;
  }
  const offsetMs = new Date(localScheduledRef).getTime() - new Date(utcScheduledRef).getTime();
  const shifted = DateTime.fromMillis(new Date(utcValue).getTime() + offsetMs);
  return shifted.isValid ? shifted.toFormat("yyyy-MM-dd'T'HH:mm:ss") : null;
}

/** Amber notice: the study snapshot predates the detected flight's monitoring data. */
function MonitoringLagBanner({
  live,
  generating,
  onRegenerate,
}: {
  live: LiveFlightMonitoring;
  generating: boolean;
  onRegenerate: () => void;
}) {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
      <RadioTower className="h-4 w-4 flex-none" />
      <span className="min-w-0 flex-1">
        Monitoring reçu après la génération de l'étude — vol {live.flightIdentifier} :{" "}
        <span className="font-semibold">{liveMonitoringLabel(live)}</span>. L'étude ne tient pas compte de ces
        données.
      </span>
      <Button size="sm" variant="outline" disabled={generating} onClick={onRegenerate}>
        {generating ? <Loader className="animate-spin" /> : <RefreshCw />}
        Régénérer l'étude
      </Button>
    </div>
  );
}

export function InitIncidentWorkflow({ claimId, onIncidentConfirmed }: InitIncidentWorkflowProps) {
  const { data: claim } = useClaim(claimId);
  // The emphasis follows the AI verdict: confirming a non-éligible incident goes against it.
  const aiSaysNotEligible = getAiEligibilityVerdict(claim) === false;
  const { assessment, debate } = useAssessmentInsight(claimId);
  // The stored INCIDENT insight (created at generation) is the canonical payload
  // we POST on confirm; the assessment-derived preview below is for display only.
  const { incident: incidentInsight } = useIncidentInsight(claimId);
  const confirmIncident = useConfirmIncident(claimId);
  const generateAssessment = useGenerateClaimAssessment(claimId);

  // Live-monitoring overlay: applied to the detected flight's entries when the insight snapshot
  // left them without monitoring. `applied` doubles as the staleness signal for the banner.
  const liveMonitoring = liveMonitoringOf(claim);
  const impactedSegmentUuid = claim?.legalSpecification?.impactedSegment?.uuid ?? null;

  function withLiveMonitoring(data: AiIncidentData): { data: AiIncidentData; applied: boolean } {
    if (!liveMonitoring || !impactedSegmentUuid) {
      return { data, applied: false };
    }
    let applied = false;
    const overlay = (flights?: AiFlightEntry[] | null) =>
      (flights ?? []).map((entry) => {
        if (entry.segment?.segmentUuid !== impactedSegmentUuid || entry.flight?.monitoringStatus) {
          return entry;
        }
        applied = true;
        // Feed the card's existing UI (barré + annulé / retard) — status plus live actuals
        // converted into the entry's naive-local referential.
        return {
          ...entry,
          flight: {
            ...entry.flight,
            monitoringStatus: liveMonitoring.status,
            actualDeparture:
              entry.flight?.actualDeparture ??
              toEntryLocal(
                liveMonitoring.actualDepartureUTC,
                liveMonitoring.scheduledDepartureUTC,
                entry.flight?.scheduledDeparture,
              ),
            actualArrival:
              entry.flight?.actualArrival ??
              toEntryLocal(
                liveMonitoring.inGateArrivalUTC,
                liveMonitoring.scheduledArrivalUTC,
                entry.flight?.scheduledArrival,
              ),
          },
        };
      });
    return {
      data: {
        ...data,
        incident: {
          ...data.incident,
          itinerary: {
            original: overlay(data.incident.itinerary?.original),
            actual: overlay(data.incident.itinerary?.actual),
          },
        },
      },
      applied,
    };
  }

  function onConfirm({ cause, consequence, sequence }: AiIncidentPreviewConfirmData) {
    if (!incidentInsight) {
      notify.error("Régénérez l'étude pour pouvoir confirmer l'incident");
      return;
    }
    // Apply the jurist's overrides + selected impacted sequence (the Vue
    // version mutated the insight in place through the sequence selector).
    const markImpacted = (flights?: AiFlightEntry[] | null) =>
      (flights ?? []).map((flight) => ({ ...flight, isImpactedSequence: flight.sequence === sequence }));
    confirmIncident.mutate(
      {
        incident: {
          ...incidentInsight.incident,
          cause,
          consequence,
          itinerary: {
            original: markImpacted(incidentInsight.incident.itinerary?.original),
            actual: markImpacted(incidentInsight.incident.itinerary?.actual),
          },
        },
        etickets: incidentInsight.etickets ?? [],
      },
      {
        onSuccess: ({ success }) => {
          if (success) {
            onIncidentConfirmed?.();
          }
        },
      },
    );
  }

  /**
   * Adapt the raw assessment-pipeline metadata into the shape `AiIncidentPreview`
   * expects. `context.itinerary` already matches the card's
   * `{ segment, flight, sequence, order, isImpactedSequence }` shape; we
   * enrich each flight with monitoringStatus + airline name from mergedFlights.
   */
  const incidentForPreview = useMemo<AiIncidentData | null>(() => {
    const raw = assessment;
    if (!raw) {
      return null;
    }
    const itinerary = raw.context?.itinerary ?? {};
    const mergedBySegment = new Map(
      (raw.preprocess?.mergedFlights ?? []).map((flight) => [flight.segmentUuid, flight]),
    );
    const cancellationDate = raw.perturbation?.notificationCommitDate ?? null;

    const enrich = (entry: AiFlightEntry): AiFlightEntry => {
      const merged = entry?.segment?.segmentUuid ? mergedBySegment.get(entry.segment.segmentUuid) : undefined;
      // Keep the entry's own status when the mergedFlights lookup misses (booked-side segmentUuids
      // aren't always in mergedFlights) — overwriting with null hid CANCELLED on "Réservation prévue".
      const monitoringStatus = merged?.monitoringStatus ?? entry.flight?.monitoringStatus ?? null;
      return {
        ...entry,
        segment: {
          ...entry.segment,
          cancellationDate: monitoringStatus === "CANCELLED" ? cancellationDate : undefined,
        },
        flight: {
          ...entry.flight,
          monitoringStatus,
          operatingAirlineName:
            typeof entry.flight?.operatingAirline === "string" ? entry.flight.operatingAirline : null,
          marketingAirlineName:
            typeof entry.flight?.marketingAirline === "string" ? entry.flight.marketingAirline : null,
        },
      };
    };

    return {
      incident: {
        itinerary: {
          original: (itinerary.original ?? []).map(enrich),
          actual: (itinerary.actual ?? []).map(enrich),
        },
        // "Résumé de la claim" — the same context summary the side card shows.
        notes: raw.context?.summary || raw.expert?.reasoning || "",
        consequence: raw.perturbation?.consequence,
        cause: raw.perturbation?.cause,
      },
      etickets: raw.context?.etickets ?? raw.preprocess?.etickets ?? [],
    };
  }, [assessment]);

  if (incidentForPreview) {
    const { data: displayIncident, applied: monitoringMissedByStudy } =
      withLiveMonitoring(incidentForPreview);
    return (
      <div>
        {monitoringMissedByStudy && liveMonitoring ? (
          <MonitoringLagBanner
            live={liveMonitoring}
            generating={generateAssessment.isPending}
            onRegenerate={() => generateAssessment.mutate("gemini")}
          />
        ) : null}
        <AiIncidentPreview
          incident={displayIncident}
          claimType={claim?.claimType?.name}
          showValidate
          loading={confirmIncident.isPending}
          onConfirm={onConfirm}
          confirmVariant={aiSaysNotEligible ? "ghost" : "default"}
          confirmActions={
            <AbandonClaimButton
              claimId={claimId}
              size="lg"
              variant={aiSaysNotEligible ? "destructive" : "ghost"}
            />
          }
          toolbox={assessment ? <AiInsightsPanel assessment={assessment} debate={debate} /> : null}
          eticketsSection={claim ? <EticketsSection claim={claim} /> : null}
          cxSection={<ClaimCxSection claimId={claimId} />}
        />
      </div>
    );
  }

  // Legacy "V3" claims: no ASSESSMENT pipeline output — render the stored
  // INCIDENT insight (already preview-shaped) in the same layout shell.
  if (incidentInsight) {
    // Hide the confirm action once the incident is created, to avoid overriding it.
    const hasValidatedIncident = !!claim?.legalSpecification?.incident;
    const { data: displayV3, applied: monitoringMissedByStudy } = withLiveMonitoring(incidentInsight);
    return (
      <div>
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700">
          Analyse héritée (V3) — sans critères d'éligibilité ni contradictions
        </div>
        {monitoringMissedByStudy && liveMonitoring ? (
          <MonitoringLagBanner
            live={liveMonitoring}
            generating={generateAssessment.isPending}
            onRegenerate={() => generateAssessment.mutate("gemini")}
          />
        ) : null}
        <AiIncidentPreview
          incident={displayV3}
          showValidate={!hasValidatedIncident}
          loading={confirmIncident.isPending}
          onConfirm={onConfirm}
          eticketsSection={claim ? <EticketsSection claim={claim} /> : null}
          cxSection={<ClaimCxSection claimId={claimId} />}
        />
      </div>
    );
  }

  return <div className="p-6 text-sm text-slate-500">Aucune analyse d'incident pour ce dossier.</div>;
}
